import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * Pricing integration APIs (new monolith endpoints).
 * Supports synchronous Mortech rate requests and PDF exports.
 */
export const pricingIntegrationsApi = createApi({
  reducerPath: 'pricingIntegrations',
  tagTypes: ['LatestSession'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    initiateMortechRates: builder.mutation({
      query: ({ data, version = 1 }) => ({
        url: `b2b/integrations/mortech/rates`,
        method: 'POST',
        body: data,
        params: version >= 2 ? { version } : {},
        headers: getHeaders(),
      }),
      invalidatesTags: ['LatestSession'],
    }),
    recordMortechSelection: builder.mutation({
      query: ({ requestId, data }) => ({
        url: `b2b/integrations/mortech/rates/${requestId}/selection`,
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['LatestSession'],
    }),
    setMortechOfferVisibility: builder.mutation({
      query: ({ applicationId, presentedToBorrower }) => ({
        url: `b2b/integrations/mortech/offers/borrower-visibility`,
        method: 'POST',
        body: { application_id: applicationId, presented_to_borrower: presentedToBorrower },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'LatestSession', id: applicationId },
      ],
    }),
    setMortechRateLock: builder.mutation({
      query: ({ applicationId, locked = true, lockPeriodDays, lockedOn }) => ({
        url: `b2b/integrations/mortech/offers/rate-lock`,
        method: 'POST',
        body: {
          application_id: applicationId,
          locked,
          ...(lockPeriodDays ? { lock_period_days: lockPeriodDays } : {}),
          ...(lockedOn ? { locked_on: lockedOn } : {}),
        },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'LatestSession', id: applicationId },
      ],
    }),
    getMortechLatestSession: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/integrations/mortech/rates/latest`,
        method: 'GET',
        params: { application_id: applicationId },
        headers: getHeaders(),
      }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'LatestSession', id: applicationId },
      ],
      keepUnusedDataFor: 300,
    }),
    exportMortechPdf: builder.mutation({
      query: ({ requestId, data }) => ({
        url: `b2b/integrations/mortech/rates/${requestId}/pdf`,
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
    }),
  }),
});

export const {
  useInitiateMortechRatesMutation,
  useRecordMortechSelectionMutation,
  useSetMortechOfferVisibilityMutation,
  useSetMortechRateLockMutation,
  useGetMortechLatestSessionQuery,
  useExportMortechPdfMutation,
} = pricingIntegrationsApi;
